import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import { Auth0Service } from '../api-auth';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { HttpLoaderFactory } from './app.module';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { AuthModule } from '@auth0/auth0-angular';
import { ConfigService } from './shared/services/config.service';
import { CookieModule } from 'ngx-cookie';
import { AppRoutingModule } from './app-routing.module';
import { BrowserModule } from '@angular/platform-browser';
import { ApiModule } from '../api-main';
import { ApiModule as InvoiceApiModule } from '../api-invoice';
import { CacheModule } from './shared/modules/cache.module';
import { MaterialModule } from './shared/modules/material.module';
import { Auth0Interceptor } from './shared/interceptors/auth0.interceptor';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';
import { LangInterceptor } from './shared/interceptors/lang.interceptor';
import { provideAnimations, provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { OrganizationInterceptor } from './shared/interceptors/organization.interceptor';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { dateFormatsFactory } from './shared/modules/DateFormat';
import { environment } from '../environments/environment';

// Sentry is disabled under Cypress (E2E) and on the local dev server (localhost:4200), matching main.ts.
const sentryEnabled = window.location.host !== 'localhost:4200' && !('Cypress' in window);

export const appConfig: ApplicationConfig = {
  providers: [
    Auth0Service,
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
      }),
      AuthModule.forRoot(ConfigService.getAuthConfig()),
      CookieModule.withOptions(),
      AppRoutingModule, //withComponentInputBinding
      BrowserModule,
      ApiModule,
      InvoiceApiModule,
      CacheModule,
      MaterialModule, //NEED to be here
      MatMomentDateModule,
    ),
    { provide: HTTP_INTERCEPTORS, useClass: Auth0Interceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LangInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: OrganizationInterceptor, multi: true },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: dateFormatsFactory,
      deps: [TranslateService],
    },
    // {
    //   provide: LOCALE_ID,
    //   deps: [LocaleSessionService],
    //   useFactory: (localeSessionService) => localeSessionService.locale,
    // },
    // Turning off animations to make E2E tests more predictable and stable
    environment.e2e ? provideNoopAnimations() : provideAnimations(),
    ...(environment.e2e
      ? [
          {
            provide: MAT_RIPPLE_GLOBAL_OPTIONS,
            useValue: { disabled: true },
          },
        ]
      : []),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),
    // Register Sentry providers only when Sentry is enabled (off under Cypress / localhost:4200).
    // Without these, Angular falls back to its default ErrorHandler.
    ...(sentryEnabled
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),
  ],
};
